<template>
  <v-app>
    <core-drawer />

    <core-view />

    <vue-progress-bar></vue-progress-bar>
    <notifications
      classes="notification-custom"
      group="notification"
      position="bottom center"
      width="100%"
    />
  </v-app>
</template>

<script>
import firebase from "firebase/app";
import "firebase/database";

export default {
  name: "App",
  data() {
    return {
      firebaseRef: null,
      initializedNotifications: false,
      processedNotificationKeys: new Set(),
    };
  },
  mounted() {
    // Wait a bit for store to be ready, then set up notifications
    this.$nextTick(() => {
      setTimeout(() => {
        this.setupSharedFilesNotifications();
      }, 1000);
    });
  },
  watch: {
    "$store.state.authentication.user"(newUser, oldUser) {
      // Reinitialize notifications if user changes (login/logout)
      if (newUser !== oldUser) {
        if (this.firebaseRef) {
          this.firebaseRef.off();
          this.firebaseRef = null;
        }
        this.initializedNotifications = false;
        this.processedNotificationKeys.clear();

        if (newUser) {
          setTimeout(() => {
            this.setupSharedFilesNotifications();
          }, 500);
        }
      }
    },
  },
  beforeDestroy() {
    if (this.firebaseRef) {
      this.firebaseRef.off();
      this.firebaseRef = null;
    }
  },
  methods: {
    async setupSharedFilesNotifications() {
      // Request notification permission
      if ("Notification" in window) {
        if (Notification.permission === "default") {
          await Notification.requestPermission();
        }
      }

      // Get current user from store
      const currentUser = this.$store.state.authentication.user;
      if (!currentUser) {
        // Retry after a delay if user is not yet available
        setTimeout(() => {
          this.setupSharedFilesNotifications();
        }, 2000);
        return;
      }

      // Initialize Firebase
      const firebaseConfig = {
        apiKey: "AIzaSyCtYeCL8HkqlMKJh6g6jcDvoH60-woVbDE",
        authDomain: "utaw.firebaseapp.com",
        databaseURL: "https://utaw.firebaseio.com",
        projectId: "project-683582246459980907",
        storageBucket: "project-683582246459980907.appspot.com",
        messagingSenderId: "193425158146",
        appId: "1:193425158146:web:f1ae134a803fb75832371b",
      };

      if (!firebase.apps.length) {
        firebase.initializeApp(firebaseConfig);
      }

      // Set up listener for shared files notifications
      const ref = firebase.database().ref("shared-files-notifications");

      // Query for notifications for the current user
      const query = ref
        .orderByChild("User")
        .equalTo(currentUser)
        .limitToLast(100);

      this.firebaseRef = query;

      // First, get existing notifications to mark them as processed
      query.once("value", (snapshot) => {
        if (snapshot.exists()) {
          const notifications = snapshot.val();
          Object.keys(notifications).forEach((key) => {
            this.processedNotificationKeys.add(key);
          });
        }
        this.initializedNotifications = true;
      });

      // Listen for new notifications (child_added fires for existing children first, then new ones)
      query.on("child_added", (snapshot) => {
        const notification = snapshot.val();
        const notificationKey = snapshot.key;

        // If we've already initialized, this is a new notification
        if (this.initializedNotifications) {
          // Check if we've already processed this notification
          if (!this.processedNotificationKeys.has(notificationKey)) {
            this.showBrowserNotification(notification);
            this.processedNotificationKeys.add(notificationKey);
          }
        }
      });
    },
    showBrowserNotification(notification) {
      const fileCount = notification.FileNames.length || 0;
      const fileText = fileCount === 1 ? "file" : "files";
      const fileNameText =
        notification.FileNames.length > 0
          ? notification.FileNames[0] +
            (fileCount > 1 ? ` and ${fileCount - 1} more` : "")
          : "files";

      const title = "New Shared File" + (fileCount > 1 ? "s" : "");
      const body = notification.UploadedBy
        ? `${notification.UploadedBy} shared ${fileCount} ${fileText}: ${fileNameText}`
        : `You have ${fileCount} new shared ${fileText}`;

      if (window.location.hostname === "localhost") {
        this.showInAppNotification(title, body);
        return;
      }

      if (!("Notification" in window)) {
        console.warn("[Notifications] Browser does not support the Notification API");
        this.showInAppNotification(title, body);
        return;
      }

      if (Notification.permission !== "granted") {
        console.warn(
          "[Notifications] Browser notification permission is not granted:",
          Notification.permission
        );
        this.showInAppNotification(title, body);
        return;
      }

      try {
        const options = {
          body: body,
          icon: "https://utaw.blob.core.windows.net/images/logo+uta11.png",
          badge: "https://utaw.blob.core.windows.net/images/logo+uta11.png",
          tag: `shared-file-${Date.now()}`,
          requireInteraction: false,
        };

        const browserNotification = new Notification(title, options);

        browserNotification.onerror = (err) => {
          console.warn("[Notifications] Browser notification error:", err);
          this.showInAppNotification(title, body);
        };

        browserNotification.onclick = () => {
          window.focus();
          this.$router.push("/files-shared-with-user").catch(() => {});
          browserNotification.close();
        };

        setTimeout(() => {
          browserNotification.close();
        }, 10000);
      } catch (err) {
        console.warn("[Notifications] Failed to show browser notification:", err);
        this.showInAppNotification(title, body);
      }
    },
    showInAppNotification(title, body) {
      this.$notify({
        group: "notification",
        title: title,
        text: body,
        duration: 15000,
        type: "info",
      });
    },
  },
};
</script>

<style lang="scss">
@import "@/styles/index.scss";

/* Remove in 1.2 */
.v-datatable thead th.column.sortable i {
  vertical-align: unset;
}
</style>
